import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, FormLabel, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../../layouts/PageTitle';
import ModalPage from '../../../components/Common/Modal/ModalPage';
import HintTooltip from '../../../components/Common/HintTooltip';
import CopyButton from '../../../components/Common/CopyButton';
import InputText from '../../../components/Forms/Element/InputText';
import CustomSelect from '../../../components/Forms/Element/CustomSelect';
import { errorAlert, successAlert, warnAlert } from '../../../components/alert/defaultAlert';
import WhatsappConnectionService from '../../../../services/superadmin/WhatsappConnectionService';
import CompanyProvisionService from '../../../../services/superadmin/CompanyProvisionService';
import { formatDateTime } from '../../../../utils/DateUtils';
import './WhatsappConnectionList.css';

const EMPTY_FORM = { companyId: null, phoneNumberId: '', businessAccountId: '', apiToken: '', appSecret: '' };

// Conexão do WhatsApp das empresas pela Plataforma, para quem usa app próprio da Meta enquanto o
// app Colmeia não tem aprovação de provedor. Só o super admin acessa (rota superAdminOnly e
// checagem no backend).
const WhatsappConnectionList = () => {
    const { t } = useTranslation();
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [companiesLoading, setCompaniesLoading] = useState(false);
    const [setupInfo, setSetupInfo] = useState({ verifyToken: '', webhookPath: '/whatsapp/webhook' });
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [warnings, setWarnings] = useState([]);

    // Recarrega a lista de conexões (com a forma de conexão e o último webhook recebido).
    const loadConnections = useCallback(async () => {
        setLoading(true);
        try {
            const response = await WhatsappConnectionService.list();
            setConnections(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            errorAlert(error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Carrega empresas para o seletor e os dados do webhook do Colmeia para as instruções.
    useEffect(() => {
        loadConnections();
        const loadCompanies = async () => {
            setCompaniesLoading(true);
            try {
                const firstPage = await CompanyProvisionService.pageable({ page: 0, size: 500 });
                const firstContent = firstPage.data?.content ?? firstPage.data;
                let allCompanies = Array.isArray(firstContent) ? firstContent : [];

                // A tela precisa listar também quem ainda não tem configuração; busca as outras
                // páginas para que a classificação não pareça completa quando há muitas empresas.
                if (Array.isArray(firstPage.data?.content) && firstPage.data.totalPages > 1) {
                    const remainingPages = await Promise.all(
                        Array.from({ length: firstPage.data.totalPages - 1 }, (_, index) => (
                            CompanyProvisionService.pageable({ page: index + 1, size: 500 })
                        ))
                    );
                    allCompanies = [
                        ...allCompanies,
                        ...remainingPages.flatMap((page) => page.data?.content || [])
                    ];
                }

                setCompanies(allCompanies.map((company) => ({ value: company.id, label: company.name })));
            } catch (error) {
                errorAlert(error);
            } finally {
                setCompaniesLoading(false);
            }
        };
        loadCompanies();
        WhatsappConnectionService.setupInfo()
            .then((response) => setSetupInfo({
                verifyToken: response.data?.verifyToken || '',
                webhookPath: response.data?.webhookPath || '/whatsapp/webhook'
            }))
            .catch(() => {});
    }, [loadConnections]);

    // Abre o formulário para uma empresa nova ou para editar a conexão existente (segredos ficam em branco).
    const openForm = (connection = null, company = null) => {
        setEditing(connection);
        setWarnings([]);
        setForm(connection ? {
            companyId: connection.companyId,
            phoneNumberId: connection.phoneNumberId || '',
            businessAccountId: connection.businessAccountId || '',
            apiToken: '',
            appSecret: ''
        } : { ...EMPTY_FORM, companyId: company?.value || null });
        setShowModal(true);
    };

    // Atualiza um campo do formulário.
    const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

    // Valida na Meta e salva; avisos (App Secret não conferido, inscrição não confirmada) ficam visíveis no modal.
    const handleSave = async () => {
        if (!form.companyId || !form.phoneNumberId.trim() || !form.businessAccountId.trim()) {
            warnAlert(t('superadmin_whatsapp.required_fields'));
            return;
        }
        const ownAppAlready = editing?.ownApp;
        if (!ownAppAlready && (!form.apiToken.trim() || !form.appSecret.trim())) {
            warnAlert(t('superadmin_whatsapp.secrets_required'));
            return;
        }
        setSaving(true);
        try {
            const response = await WhatsappConnectionService.connectManually(form.companyId, {
                phoneNumberId: form.phoneNumberId.trim(),
                businessAccountId: form.businessAccountId.trim(),
                apiToken: form.apiToken.trim() || null,
                appSecret: form.appSecret.trim() || null
            });
            const responseWarnings = response.data?.warnings || [];
            setWarnings(responseWarnings);
            setForm((current) => ({ ...current, apiToken: '', appSecret: '' }));
            setEditing(response.data?.connection || editing);
            successAlert(t('superadmin_whatsapp.saved'));
            if (responseWarnings.length === 0) {
                setShowModal(false);
            }
            loadConnections();
        } catch (error) {
            errorAlert(error);
        } finally {
            setSaving(false);
        }
    };

    const webhookUrl = `${(process.env.REACT_APP_API_URL || '').replace(/\/$/, '')}${setupInfo.webhookPath}`;
    const selectedCompany = companies.find((company) => company.value === form.companyId) || null;
    const connectionByCompanyId = new Map(connections.map((connection) => [connection.companyId, connection]));
    const companyRows = companies.map((company) => ({ ...company, connection: connectionByCompanyId.get(company.value) || null }));
    const configuredCount = companyRows.filter((company) => company.connection).length;
    const unconfiguredCount = Math.max(0, companyRows.length - configuredCount);
    const summaryCards = [
        { key: 'total', label: t('superadmin_whatsapp.companies_total'), value: companyRows.length, icon: 'fa-solid fa-building', tone: 'primary' },
        { key: 'configured', label: t('superadmin_whatsapp.configured_count'), value: configuredCount, icon: 'fa-solid fa-circle-check', tone: 'green' },
        { key: 'unconfigured', label: t('superadmin_whatsapp.not_configured_count'), value: unconfiguredCount, icon: 'fa-solid fa-circle-minus', tone: 'amber' }
    ];

    return (
        <>
            <PageTitle activeMenu={t('superadmin_whatsapp.title')} motherMenu={t('administration')} />

            <ModalPage
                title={editing ? t('superadmin_whatsapp.edit_title', { company: editing.companyName }) : t('superadmin_whatsapp.connect')}
                show={showModal}
                setShow={setShowModal}
                size="lg"
                centered
                dialogClassName="whatsapp-connection-modal"
                child={
                    <div className="whatsapp-connection-form">
                        {warnings.length > 0 && (
                            <Alert variant="warning">
                                <div className="fw-semibold mb-1">{t('superadmin_whatsapp.warnings_title')}</div>
                                <ul className="mb-0 ps-3">
                                    {warnings.map((warning) => <li key={warning}>{warning}</li>)}
                                </ul>
                            </Alert>
                        )}
                        <section className="whatsapp-connection-form__section">
                            <div className="whatsapp-connection-form__section-heading">
                                <span className="whatsapp-connection-form__step">1</span>
                                <div>
                                    <h3>{t('superadmin_whatsapp.form_company_title')}</h3>
                                    <p>{t('superadmin_whatsapp.form_company_hint')}</p>
                                </div>
                            </div>
                            <FormLabel>{t('superadmin_whatsapp.company')}</FormLabel>
                            <CustomSelect
                                value={selectedCompany}
                                onChange={(option) => setField('companyId', option?.value || null)}
                                options={companies}
                                placeholder={t('superadmin_whatsapp.select_company')}
                                isDisabled={Boolean(editing)}
                            />
                        </section>

                        <section className="whatsapp-connection-form__section">
                            <div className="whatsapp-connection-form__section-heading">
                                <span className="whatsapp-connection-form__step">2</span>
                                <div>
                                    <h3>{t('superadmin_whatsapp.form_ids_title')}</h3>
                                    <p>{t('superadmin_whatsapp.form_ids_hint')}</p>
                                </div>
                            </div>
                            <Row className="g-3">
                                <Col xs={12} md={6}>
                                    <FormLabel>Phone Number ID</FormLabel>
                                    <InputText value={form.phoneNumberId} inputMode="numeric" placeholder="Ex.: 123456789012345"
                                        onChange={(event) => setField('phoneNumberId', event.target.value)} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <FormLabel>{t('superadmin_whatsapp.waba')}</FormLabel>
                                    <InputText value={form.businessAccountId} inputMode="numeric" placeholder="Ex.: 123456789012345"
                                        onChange={(event) => setField('businessAccountId', event.target.value)} />
                                </Col>
                            </Row>
                        </section>

                        <section className="whatsapp-connection-form__section">
                            <div className="whatsapp-connection-form__section-heading">
                                <span className="whatsapp-connection-form__step">3</span>
                                <div>
                                    <h3>{t('superadmin_whatsapp.form_credentials_title')}</h3>
                                    <p>{t('superadmin_whatsapp.form_credentials_hint')}</p>
                                </div>
                            </div>
                            <Row className="g-3">
                                <Col xs={12} md={6}>
                                    <FormLabel>{t('superadmin_whatsapp.api_token')} <HintTooltip text={t('superadmin_whatsapp.token_hint')} /></FormLabel>
                                    <InputText type="password" autoComplete="new-password" value={form.apiToken}
                                        placeholder={editing?.apiTokenConfigured ? t('superadmin_whatsapp.keep_current') : t('superadmin_whatsapp.token_placeholder')}
                                        onChange={(event) => setField('apiToken', event.target.value)} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <FormLabel>App Secret <HintTooltip text={t('superadmin_whatsapp.secret_hint')} /></FormLabel>
                                    <InputText type="password" autoComplete="new-password" value={form.appSecret}
                                        placeholder={editing?.ownApp ? t('superadmin_whatsapp.keep_current') : t('superadmin_whatsapp.secret_placeholder')}
                                        onChange={(event) => setField('appSecret', event.target.value)} />
                                </Col>
                            </Row>
                        </section>

                        <details className="whatsapp-connection-form__webhook">
                            <summary>
                                <span className="whatsapp-connection-form__webhook-icon"><i className="fa-solid fa-plug" aria-hidden="true" /></span>
                                <span><strong>{t('superadmin_whatsapp.webhook_setup_title')}</strong><small>{t('superadmin_whatsapp.webhook_setup_hint')}</small></span>
                                <i className="fa-solid fa-chevron-down whatsapp-connection-form__webhook-chevron" aria-hidden="true" />
                            </summary>
                            <div className="whatsapp-connection-form__webhook-content">
                                <div className="whatsapp-connection-form__webhook-intro">
                                    <span className="whatsapp-connection-form__webhook-step">1</span>
                                    <p>{t('superadmin_whatsapp.webhook_instructions')}</p>
                                </div>
                                <div className="whatsapp-connection-form__webhook-value">
                                    <div className="whatsapp-connection-form__webhook-label"><strong><i className="fa-solid fa-link" aria-hidden="true" />{t('superadmin_whatsapp.webhook_url')}</strong><CopyButton textToCopy={webhookUrl} showText={false} /></div>
                                    <code>{webhookUrl}</code>
                                </div>
                                <div className="whatsapp-connection-form__webhook-value">
                                    <div className="whatsapp-connection-form__webhook-label"><strong><i className="fa-solid fa-key" aria-hidden="true" />{t('superadmin_whatsapp.verify_token')}</strong><CopyButton textToCopy={setupInfo.verifyToken} showText={false} /></div>
                                    <code>{setupInfo.verifyToken}</code>
                                </div>
                                <div className="whatsapp-connection-form__webhook-fields">
                                    <div className="whatsapp-connection-form__webhook-intro">
                                        <span className="whatsapp-connection-form__webhook-step">2</span>
                                        <p>{t('superadmin_whatsapp.webhook_fields')}</p>
                                    </div>
                                    <div className="whatsapp-connection-form__webhook-tags">
                                        <code>messages</code>
                                        <code>message_template_status_update</code>
                                        <code>template_category_update</code>
                                    </div>
                                </div>
                            </div>
                        </details>

                        <div className="whatsapp-connection-form__footer">
                            <span><i className="fa-solid fa-shield-halved" aria-hidden="true" />{t('superadmin_whatsapp.credentials_secure_hint')}</span>
                            <Button className="whatsapp-connection-form__save" onClick={handleSave} disabled={saving}>
                                {saving && <Spinner animation="border" size="sm" className="me-2" />}
                                {t('superadmin_whatsapp.validate_and_save')}
                            </Button>
                        </div>
                    </div>
                }
            />

            <div className="whatsapp-connection-page">
                <section className="whatsapp-connection-hero">
                    <div className="whatsapp-connection-hero__icon" aria-hidden="true">
                        <i className="fa-brands fa-whatsapp"></i>
                    </div>
                    <div className="whatsapp-connection-hero__copy">
                        <span className="whatsapp-connection-hero__eyebrow">{t('superadmin_whatsapp.section_eyebrow')}</span>
                        <h2>{t('superadmin_whatsapp.section_title')}</h2>
                        <p>{t('superadmin_whatsapp.intro')}</p>
                    </div>
                    <Button className="whatsapp-connection-hero__action" onClick={() => openForm()}>
                        <i className="fa-solid fa-plus" aria-hidden="true" />
                        <span>{t('superadmin_whatsapp.connect')}</span>
                    </Button>
                </section>

                <div className="whatsapp-connection-summary" aria-label={t('superadmin_whatsapp.summary')}>
                    {summaryCards.map((card) => (
                        <Card className={`whatsapp-connection-summary__card whatsapp-connection-summary__card--${card.tone}`} key={card.key}>
                            <Card.Body>
                                <span className="whatsapp-connection-summary__icon" aria-hidden="true"><i className={card.icon}></i></span>
                                <div>
                                    <span className="whatsapp-connection-summary__label">{card.label}</span>
                                    <strong className="whatsapp-connection-summary__value">{card.value}</strong>
                                </div>
                            </Card.Body>
                        </Card>
                    ))}
                </div>

                <Card className="whatsapp-connection-list-card">
                    <Card.Body>
                        <div className="whatsapp-connection-list-card__header">
                            <div>
                                <h3>{t('superadmin_whatsapp.connections_heading')}</h3>
                                <p>{t('superadmin_whatsapp.connections_caption')}</p>
                            </div>
                            <div className="whatsapp-connection-list-card__tools">
                                <Badge bg="light" text="dark">{companyRows.length}</Badge>
                                <Button size="sm" variant="outline-secondary" onClick={loadConnections} disabled={loading}>
                                    {loading
                                        ? <Spinner animation="border" size="sm" />
                                        : <i className="fa-solid fa-rotate-right" aria-hidden="true" />}
                                    <span className="visually-hidden">{t('reload')}</span>
                                </Button>
                            </div>
                        </div>

                    {loading || companiesLoading ? (
                        <div className="whatsapp-connection-state"><Spinner animation="border" size="sm" /><span>{t('loading')}</span></div>
                    ) : companyRows.length === 0 ? (
                        <div className="whatsapp-connection-empty">
                            <span className="whatsapp-connection-empty__icon" aria-hidden="true"><i className="fa-brands fa-whatsapp"></i></span>
                            <strong>{t('superadmin_whatsapp.empty_title')}</strong>
                            <p>{t('superadmin_whatsapp.empty')}</p>
                            <Button size="sm" variant="outline-primary" onClick={() => openForm()}>
                                <i className="fa-solid fa-plus me-1" aria-hidden="true" />{t('superadmin_whatsapp.connect')}
                            </Button>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-list i-table style-1 mb-0 border-0 whatsapp-connection-list__table">
                                <thead>
                                    <tr>
                                        <th>{t('superadmin_whatsapp.company')}</th>
                                        <th>{t('superadmin_whatsapp.number')}</th>
                                        <th>{t('superadmin_whatsapp.configuration_status')}</th>
                                        <th>{t('superadmin_whatsapp.connection')}</th>
                                        <th>{t('superadmin_whatsapp.last_webhook')}</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {companyRows.map((company) => {
                                        const connection = company.connection;
                                        return (
                                        <tr key={company.value} className={`align-middle${connection ? '' : ' whatsapp-connection-list__row--unconfigured'}`}>
                                            <td>
                                                <div className="fw-semibold">{company.label}</div>
                                                <small className="whatsapp-connection-list__waba">WABA {connection?.businessAccountId || '-'}</small>
                                            </td>
                                            <td>{connection?.phoneNumber || '-'}</td>
                                            <td>
                                                <span className={`whatsapp-connection-list__status ${connection ? 'is-configured' : 'is-unconfigured'}`}>
                                                    <i className={`fa-solid ${connection ? 'fa-circle-check' : 'fa-circle-minus'}`} aria-hidden="true" />
                                                    {connection ? t('superadmin_whatsapp.configured') : t('superadmin_whatsapp.not_configured')}
                                                </span>
                                            </td>
                                            <td>
                                                {connection ? (
                                                    <Badge bg={connection.ownApp ? 'info' : 'secondary'}>
                                                        {connection.ownApp ? t('superadmin_whatsapp.own_app') : t('superadmin_whatsapp.colmeia_app')}
                                                    </Badge>
                                                ) : <span className="text-muted">-</span>}
                                            </td>
                                            <td>
                                                {connection?.lastWebhookAt
                                                    ? <span className="whatsapp-connection-list__webhook whatsapp-connection-list__webhook--received"><i className="fa-solid fa-circle-check" aria-hidden="true" />{formatDateTime(connection.lastWebhookAt)}</span>
                                                    : connection
                                                        ? <span className="whatsapp-connection-list__webhook whatsapp-connection-list__webhook--missing"><i className="fa-solid fa-circle-exclamation" aria-hidden="true" />{t('superadmin_whatsapp.never_received')}</span>
                                                        : <span className="text-muted">-</span>}
                                            </td>
                                            <td className="text-end">
                                                <Button
                                                    size="sm"
                                                    variant={connection ? '' : 'primary'}
                                                    className={connection ? 'whatsapp-connection-list__edit-button' : undefined}
                                                    onClick={() => connection ? openForm(connection) : openForm(null, company)}
                                                >
                                                    {connection ? t('superadmin_whatsapp.edit') : t('superadmin_whatsapp.connect_short')}
                                                </Button>
                                            </td>
                                        </tr>
                                    );})}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card.Body>
                </Card>
            </div>
        </>
    );
};

export default WhatsappConnectionList;
